import {
  clockMinutes,
  dateLocale,
  daysBetween,
  formatDayMonth,
  formatWeekdayDayMonth,
  parseDateOnly,
} from '@voll-intelligence/dates';
import type { FlightMoneySource } from './flight_offer_source.js';

/*
 * Formatadores puros dos resultados de voo (#4997): a lista de ofertas, o
 * detalhe, as tarifas e os cards do chat formatam pela MESMA régua, e ela mora
 * aqui. O genui-kit reexporta estas funções no `flight_format` dele, que guarda
 * só as que dependem do catálogo de textos.
 *
 * Datas chegam como string sem garantia de ISO (provider http): extrai por
 * regex e cai pra vazio em vez de `Invalid Date`. Sem parse com Date e sem
 * conversão de fuso: mostra o horário EXATAMENTE como veio do provider.
 */

type LocaleInput = string | null | undefined;

export function fmtTime(s: string): string {
  return /T(\d{2}:\d{2})/.exec(s)?.[1] ?? '';
}

export function fmtDate(s: string, locale: LocaleInput = 'pt-BR'): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return '';
  return dateLocale(locale) === 'en'
    ? `${String(m[2])}/${String(m[3])}`
    : `${String(m[3])}/${String(m[2])}`;
}

/*
 * As datas da régua (#3990, #4124) vêm do `@voll-intelligence/dates`: tabelas
 * fixas por idioma (não `Intl`, que varia entre engines) e a regra do ano — por
 * extenso só na última data, quando alguma cai fora do ano corrente. Aqui só se
 * lê o ISO cru do provider: o que não for uma data real vira vazio.
 */

/** "Qua, 24 jun" a partir de uma data ISO (sem fuso), nunca com ano. Vazio se
 *  não for uma data real. */
export function fmtWeekdayDate(s: string, locale: LocaleInput = 'pt-BR'): string {
  const date = parseDateOnly(s);
  return date ? formatWeekdayDayMonth(date, locale) : '';
}

/** As datas de `yearOf` que decidem o ano; uma ilegível conta como a própria. */
function yearSpan(date: Date, yearOf: readonly string[]): Date[] {
  return yearOf.map((iso) => parseDateOnly(iso) ?? date);
}

/** ISO → "25 dez" (mesmo ano) ou "3 jan 2027" (fora do ano corrente). Rótulo de
 *  data sem dia da semana (hotel_rooms). Vazio quando não é
 *  uma data real. `yearOf` é a régua das janelas: a ponta inicial passa `[]`
 *  (nunca leva ano) e a final passa as duas pontas, para o ano entrar nela
 *  quando QUALQUER uma cai fora do ano corrente. */
export function fmtDayMonth(
  s: string,
  locale: LocaleInput = 'pt-BR',
  yearOf: readonly string[] = [s],
): string {
  const date = parseDateOnly(s);
  return date ? formatDayMonth(date, locale, new Date(), yearSpan(date, yearOf)) : '';
}

/** ISO → "Qua, 25 dez" (mesmo ano) ou "Sex, 3 jan 2027" (fora do ano corrente).
 *  Rótulo de trecho com dia da semana (cabeçalho da perna,
 *  FlightReservationCard). Vazio quando a data não é real (rollover como 02-30
 *  daria um dia da semana errado). `yearOf` como em `fmtDayMonth`. */
export function fmtWeekdayDayMonth(
  s: string,
  locale: LocaleInput = 'pt-BR',
  yearOf: readonly string[] = [s],
): string {
  const date = parseDateOnly(s);
  return date ? formatWeekdayDayMonth(date, locale, new Date(), yearSpan(date, yearOf)) : '';
}

/** Duração do trecho "1h20min" / "45min" a partir dos HH:MM crus (sem fuso).
 *  Chegada antes da partida = voo cruza a meia-noite (+24h). Vazio se não casar. */
export function fmtDuration(departAt: string, arriveAt: string): string {
  const a = clockMinutes(departAt);
  const b = clockMinutes(arriveAt);
  if (a == null || b == null) return '';
  let diff = b - a;
  if (diff < 0) diff += 24 * 60;
  const h = Math.floor(diff / 60);
  const min = diff % 60;
  if (h && min) return `${String(h)}h${String(min)}min`;
  if (h) return `${String(h)}h`;
  return `${String(min)}min`;
}

/** Dias de calendário entre chegada e partida (chegada − partida), pelas datas
 *  ISO 'YYYY-MM-DD' das strings (sem fuso, como as irmãs). 0 quando mesma data,
 *  não parseia, ou negativo (dado inválido). Alimenta o indicador "+N" de chegada
 *  em dia seguinte nas linhas de trecho (voos overnight). Datas irreais contam
 *  como não parseadas. */
export function arrivalDayOffset(departAt: string, arriveAt: string): number {
  return daysBetween(departAt, arriveAt);
}

// Moeda NÃO é localizada: renderiza sempre em formato brasileiro (R$ 1.234,56),
// independente do locale da UI, usando o código de moeda vindo do backend. Só
// texto e datas seguem o idioma do navegador.
export function fmtMoney(p: FlightMoneySource): string {
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: p.currency,
    }).format(p.amount);
  } catch {
    return `${p.currency} ${p.amount.toFixed(2)}`;
  }
}

/** Só o SÍMBOLO da moeda ("R$", "US$") — o prefixo dos campos de valor da
 *  régua de preço (#2110), onde o número é um input editável e o símbolo mora
 *  fora dele. Mesma convenção do `fmtMoney`: formato brasileiro, sempre. */
export function currencySymbol(currency: string): string {
  try {
    const symbol = new Intl.NumberFormat('pt-BR', { style: 'currency', currency })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value;
    // `¤` é o placeholder do CLDR para moeda sem símbolo — o código diz mais.
    return !symbol || symbol === '¤' ? currency : symbol;
  } catch {
    return currency;
  }
}

/**
 * Moeda SEM centavos (`R$ 1.182`) — a régua do card de voo e da escada de
 * tarifas, onde a tela compara meia dúzia de preços de uma vez e os centavos
 * viram ruído em toda coluna de número.
 *
 * **Arredonda, não trunca.** O design-hub trunca (`Math.floor`), e truncar numa
 * superfície de compra mostra menos do que se paga — R$ 2.990,80 apareceria como
 * `R$ 2.990`. Arredondar erra para os dois lados e nunca subestima
 * sistematicamente. O valor exato, com centavos, segue no checkout, que é onde a
 * pessoa autoriza.
 */
export function fmtMoneyWhole(p: FlightMoneySource): string {
  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: p.currency,
      maximumFractionDigits: 0,
    }).format(p.amount);
  } catch {
    return `${p.currency} ${String(Math.round(p.amount))}`;
  }
}
